import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../services/api';
import { websocketService } from '../services/websocket';
import { useAuth } from './AuthContext';

interface DataContextType {
  selectedDataId: number | null;
  setSelectedDataId: (id: number | null) => void;
  lastDataVersion: string | null;
  isNewDataAvailable: boolean;
  checkForNewData: () => Promise<void>;
  triggerDataRefresh: () => void;
  lastCheckTime: Date | null;
  shouldAutoSelectLatest: boolean;
  setShouldAutoSelectLatest: (should: boolean) => void;
  websocketConnected: boolean;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const { token, isLoading } = useAuth();
  const [selectedDataId, setSelectedDataId] = useState<number | null>(null);
  const [lastDataVersion, setLastDataVersion] = useState<string | null>(null);
  const [isNewDataAvailable, setIsNewDataAvailable] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState<Date | null>(null);
  const [shouldAutoSelectLatest, setShouldAutoSelectLatest] = useState(false);
  const [websocketConnected, setWebsocketConnected] = useState(false);

  // Function to trigger data refresh (can be called from components)
  const triggerDataRefresh = useCallback(() => {
    // Dispatch a custom event that components can listen to for data refresh
    window.dispatchEvent(new CustomEvent('dataRefreshRequested'));
  }, []);

  // Check for new data by comparing timestamps
  const checkForNewData = useCallback(async () => {
    try {
      const dataVersion = await apiService.getDataVersion();
      const currentTimestamp = dataVersion.timestamp;
      
      // Update last check time
      setLastCheckTime(new Date());
      
      if (lastDataVersion && currentTimestamp && currentTimestamp > lastDataVersion) {
        console.log('🆕 New data detected! Auto-refreshing...');
        setIsNewDataAvailable(true);
        setShouldAutoSelectLatest(true); // Set flag to auto-select on next refresh
        
        // // Show toast notification
        // toast.success('🆕 New data available! Auto-refreshing...', {
        //   duration: 3000,
        //   position: 'top-right',
        // });
        
        // Auto-refresh data after a short delay to ensure backend processing is complete
        setTimeout(() => {
          setIsNewDataAvailable(false);
          // Trigger data refresh
          triggerDataRefresh();
        }, 2000);
      }
      
      setLastDataVersion(currentTimestamp);
    } catch (error) {
      console.error('Error checking for new data:', error);
    }
  }, [lastDataVersion, triggerDataRefresh]);

  // Set up periodic data version checking
  useEffect(() => {
    // Check immediately on mount
    checkForNewData();

    // Set up interval to check every 30 seconds
    const interval = setInterval(checkForNewData, 30000);

    return () => clearInterval(interval);
  }, [checkForNewData]);

  // Store last data version in localStorage for persistence across sessions
  useEffect(() => {
    if (lastDataVersion) {
      localStorage.setItem('lastDataVersion', lastDataVersion);
    }
  }, [lastDataVersion]);

  // Load last data version from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('lastDataVersion');
    if (stored) {
      setLastDataVersion(stored);
    }
  }, []);

  // WebSocket integration for real-time sync notifications
  useEffect(() => {
    // Set up WebSocket connection state handler
    const removeConnectionHandler = websocketService.onConnectionStateChange((connected) => {
      setWebsocketConnected(connected);
      console.log(`🔌 WebSocket connection state changed: ${connected ? 'connected' : 'disconnected'}`);
    });

    // Set up WebSocket message handler for sync completion events
    const removeMessageHandler = websocketService.onMessage((message) => {
      switch (message.type) {
        case 'sync_completed':
          console.log('🎉 Data sync completed');
          
          // Show toast notification
          toast.success('🔄 Data sync completed', {
            duration: 3000,
            position: 'top-right',
          });

          // Trigger immediate data refresh
          setIsNewDataAvailable(true);
          setShouldAutoSelectLatest(true);
          
          // Trigger refresh after a short delay to ensure backend processing is complete
          setTimeout(() => {
            setIsNewDataAvailable(false);
            triggerDataRefresh();
          }, 1000);
          break;
          
        case 'manual_override_updated':
          console.log('📝 Manual override updated');
          
          // Dispatch custom event for immediate Manual tab refresh
          window.dispatchEvent(new CustomEvent('manualOverrideUpdated', {
            detail: { status: message.status, message: message.message }
          }));
          
          // Also trigger forecast update event since override changes will trigger forecast recalculation
          // This ensures schedules and recommendations are refreshed on the dashboard
          window.dispatchEvent(new CustomEvent('forecastUpdated', {
            detail: { status: message.status, message: message.message }
          }));
          break;
          
        case 'forecast_updated':
          console.log('📊 Forecast data updated');
          
          // Show appropriate toast notification
          if (message.status === 'success') {
            toast.success('📊 Forecast data updated', {
              duration: 3000,
              position: 'top-right',
            });
          } else if (message.status === 'error') {
            toast.error(`📊 Forecast update failed: ${message.message}`, {
              duration: 5000,
              position: 'top-right',
            });
          }
          
          // Dispatch custom event for dashboard refresh (charts, schedules, recommendations, overrides)
          window.dispatchEvent(new CustomEvent('forecastUpdated', {
            detail: { status: message.status, message: message.message }
          }));
          break;
          
        case 'battery_schedules_updated':
          // Legacy support - redirect to forecast_updated handler
          console.log('🔋 Battery schedules updated (legacy event)');
          window.dispatchEvent(new CustomEvent('forecastUpdated', {
            detail: { status: message.status, message: message.message }
          }));
          break;
          
        case 'sync_error':
          console.error('❌ Sync error:', message.message);
          
          // Show error toast
          toast.error(`❌ Sync error: ${message.message}`, {
            duration: 5000,
            position: 'top-right',
          });
          break;
          
        default:
          // Ignore unknown message types
          break;
      }
    });

    if (!isLoading && token) {
      websocketService.disconnect();
      websocketService.connect();
    } else if (!isLoading && !token) {
      websocketService.disconnect();
    }

    return () => {
      removeConnectionHandler();
      removeMessageHandler();
      websocketService.disconnect();
    };
  }, [triggerDataRefresh, token, isLoading]);

  return (
    <DataContext.Provider value={{ 
      selectedDataId, 
      setSelectedDataId, 
      lastDataVersion,
      isNewDataAvailable,
      checkForNewData,
      triggerDataRefresh,
      lastCheckTime,
      shouldAutoSelectLatest,
      setShouldAutoSelectLatest,
      websocketConnected
    }}>
      {children}
    </DataContext.Provider>
  );
}; 